import * as React from "react";

import { Checkbox } from "@/lib/shadcn/ui/checkbox";
import { Label } from "@/lib/shadcn/ui/label";
import SearchableSelect, { SelectOption } from "@/lib/shadcn/components/searchable-select";
import { useGetHostHubRentalsQuery } from "@/store/hosthub-api";

interface HostHubRentalSelectorProps {
    /** The currently-mapped HostHub rental id, or undefined when unmapped. */
    rentalId?: string;
    /** Whether this apartment is marked "Not channel-managed" (hosthubExcluded). */
    excluded: boolean;
    /** Called with the chosen rental id (or undefined to clear the mapping). */
    onChange: (rentalId: string | undefined) => void;
    /** Called when the "Not channel-managed" checkbox toggles. */
    onExcludedChange: (excluded: boolean) => void;
    /** Disable both controls (the apartment detail form is read-only until "Edit"). */
    disabled?: boolean;
    /** Mark the field as required (asterisk on the label); used on the add form. */
    required?: boolean;
}

/**
 * HostHubRentalSelector is the manual-resolution control (ADR-013, FR-021,
 * hosthub-reservation-ingestion): a searchable list of HostHub rentals (GET
 * /admin/hosthub/rentals) bound to the apartment's `hosthubRentalId`, plus a
 * "Not channel-managed" checkbox bound to `hosthubExcluded`. Embedded in the
 * apartment edit form; persisted via the existing apartment PUT mutation.
 */
export const HostHubRentalSelector: React.FC<HostHubRentalSelectorProps> = ({
    rentalId,
    excluded,
    onChange,
    onExcludedChange,
    disabled = false,
    required = false,
}) => {
    const { data: rentals = [] } = useGetHostHubRentalsQuery();
    // Stable id so the trigger can advertise the format hint via aria-describedby.
    const hintId = React.useId();

    // Surface the rental id next to the name so the operator can match it against
    // the HostHub URL (the id is the segment after rentals/).
    const options: SelectOption[] = rentals.map((rental) => ({
        value: rental.id,
        label: `${rental.name} — ${rental.id}`,
    }));

    return (
        <div data-testid="hosthub-rental-field" className="space-y-4">
            <div className="space-y-2">
                <Label htmlFor="hosthubRental">
                    HostHub rental
                    {required && <span aria-hidden="true">*</span>}
                </Label>
                <SearchableSelect
                    id="hosthubRental"
                    variant="outline"
                    width="w-full"
                    placeholder="Select HostHub rental"
                    options={options}
                    selectedValue={rentalId ?? ""}
                    onChange={(value) => onChange(value === "" ? undefined : value)}
                    disabled={disabled}
                    data-testid="hosthub-rental-select"
                    aria-required={required}
                    aria-describedby={hintId}
                />
                <p id={hintId} className="text-xs text-muted-foreground">
                    The id is the segment after <code>rentals/</code> in the rental&apos;s HostHub URL — e.g.{" "}
                    <code>app.hosthub.com/z/rentals/ke5ahzmxgr/edit</code> → <code>ke5ahzmxgr</code>.
                </p>
            </div>
            <div className="flex items-center gap-2">
                <Checkbox
                    id="hosthubExcluded"
                    data-testid="hosthub-excluded-checkbox"
                    checked={excluded}
                    disabled={disabled}
                    onCheckedChange={(checked) => onExcludedChange(checked === true)}
                />
                <Label htmlFor="hosthubExcluded" className="cursor-pointer">
                    Not channel-managed
                </Label>
            </div>
        </div>
    );
};

export default HostHubRentalSelector;
